<template>
  <v-container class="d-flex flex-column fill-height pt-0">
    <LoadingCircle v-if="isLoading" />
    <v-row v-else style="width: 100%">
      <v-col class="q-side-menu hidden-md-and-down mr-4">
        <SideMenu
          :items="sectionItems"
          :active-item="currentSectionIndex"
          :loading="false"
          @new-section="scrollToSection"
        />
      </v-col>
      <v-col class="d-flex flex-column">
        <StandardCardPageContainer>
          <v-row>
            <v-col>
              <h1 class="text-left">Questionnaire Answers</h1>
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col class="ml-4 mb-4" cols="6">
              <p class="text-left">
                <strong>Questionnaire: </strong
                >{{ reportViewStore.selectedQuestionnaireLabel }}
              </p>
              <p class="text-left">
                <strong>Company:</strong>
                {{ reportViewStore.selectedReceiverCompanyName }}
              </p>
            </v-col>
          </v-row>
          <v-card
            v-for="reportSet in reportSets"
            class="mb-10"
            :key="reportSet.id"
            :id="reportSet.id"
            flat
            v-intersect="{
              handler: onIntersect,
              options: {
                threshold: 0.25,
                delay: 750,
              },
            }"
          >
            <v-card-title>{{ reportSet.title }}</v-card-title>
            <v-divider />
            <v-card-text
              v-for="(itemRow, index) in reportSet.items"
              :key="index"
            >
              <QuestionSelector
                v-for="item in itemRow"
                :key="item.questionModel.question.id"
                :is-report-mode="true"
                :question-model="item.questionModel"
                :answer="item.answers"
              />
            </v-card-text>
          </v-card>
        </StandardCardPageContainer>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { useReportViewStore } from "@/stores/reportViewStore";
import { onBeforeMount, ref, type Ref, computed } from "vue";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import QuestionSelector from "@/components/questionnaire/QuestionSelector.vue";
import type { QuestionnaireItemSetDto } from "@/models/dto/questionnaireItemSetDto";
import { QuestionnaireLanguage } from "@/models/enum/questionnaireLanguage";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { QuestionnaireSideMenuItem } from "@/models/questionnaireSideMenuItem";
import SideMenu from "@/components/custom/QuestionnaireSideMenu.vue";

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const reportViewStore = useReportViewStore();
const reportSets: Ref<QuestionnaireItemSetDto[]> = ref([]);
const isLoading = ref(false);
const currentSectionIndex = ref(0);

onBeforeMount(async () => {
  isLoading.value = true;
  reportSets.value = await selectedQuestionnaireStore.getSelectedQuestionnaire(
    reportViewStore.selectedContextId,
    QuestionnaireLanguage.En
  );
  isLoading.value = false;
});

const sectionItems = computed(() =>
  reportSets.value.map(
    (item) => new QuestionnaireSideMenuItem(item.title, item.answerStatus)
  )
);

async function scrollToSection(index: number) {
  currentSectionIndex.value = index;
  const section = reportSets.value.at(index);
  if (section !== undefined) {
    const position = document.getElementById(section.id)?.offsetTop;
    window.scrollTo({ top: position, behavior: "smooth" });
  }
}

function onIntersect(isIntersecting: boolean, entries: any, observer: any) {
  if (!isIntersecting) {
    return;
  }
  const entry = entries[0];
  const id = entry.target.id;
  const index = reportSets.value.findIndex((x) => x.id == id);

  if (index != -1 && entry.intersectionRatio > 0.25) {
    currentSectionIndex.value = index;
  }
}
</script>

<style scoped lang="scss">
.q-side-menu {
  max-width: 300px;
}
</style>
